import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CreditCard, EllipsisVertical } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dropdown,
	DropdownButton,
	DropdownItem,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import { Heading } from "@/components/catalyst/Heading";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/catalyst/Table";
import { Fab } from "@/components/Fab";
import { Time } from "@/components/Time";
import { orpc } from "@/orpc/client";
import { AddPaymentMethodDialog } from "./components/AddPaymentMethodDialog";
import { RenamePaymentMethodDialog } from "./components/RenamePaymentMethodDialog";
import { ReplacePaymentMethodDialog } from "./components/ReplacePaymentMethodDialog";

function getCardBrandIcon(brand: string) {
	// Capitalize first letter
	return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export function PaymentMethods() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const search = useSearch({ from: "/_app/finances/" });

	const isAddDialogOpen = !!search.add;
	const successRedirect = search.successRedirect ?? "";

	const setIsAddDialogOpen = (value: boolean | null) => {
		navigate({
			to: "/finances",
			search: (prev) => ({
				...prev,
				tab: prev.tab as "earnings" | "payouts" | "payment_methods",
				add: value ? true : undefined,
			}),
		});
	};

	// Dialogs state
	const [renameEntityId, setRenameEntityId] = useState<string | null>(null);
	const [renameEntityName, setRenameEntityName] = useState<string>("");
	const [replaceEntityId, setReplaceEntityId] = useState<string | null>(null);

	const { data: entities } = useSuspenseQuery(
		orpc.billingEntities.list.queryOptions({
			input: undefined,
		}),
	);

	const { mutate: deleteEntity } = useMutation(
		orpc.billingEntities.delete.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.billingEntities.list.key(),
				});
				toast.success("Payment method deleted");
			},
			onError: (error) => {
				toast.error(`Failed to delete: ${error.message}`);
			},
		}),
	);

	const handleDelete = (id: string, name: string) => {
		if (
			confirm(
				`Are you sure you want to delete "${name}"? This cannot be undone.`,
			)
		) {
			deleteEntity({ id });
		}
	};

	const handleRename = (id: string, currentName: string) => {
		setRenameEntityId(id);
		setRenameEntityName(currentName);
	};

	const handleReplace = (id: string) => {
		setReplaceEntityId(id);
	};

	return (
		<>
			<AddPaymentMethodDialog
				isOpen={isAddDialogOpen}
				onClose={() => setIsAddDialogOpen(null)}
				successRedirect={successRedirect || null}
			/>
			<RenamePaymentMethodDialog
				isOpen={renameEntityId !== null}
				onClose={() => setRenameEntityId(null)}
				entityId={renameEntityId}
				currentName={renameEntityName}
			/>
			<ReplacePaymentMethodDialog
				isOpen={replaceEntityId !== null}
				onClose={() => setReplaceEntityId(null)}
				entityId={replaceEntityId}
			/>

			<div>
				<div className="flex items-center justify-between">
					<Heading level={2}>Payment Methods</Heading>
				</div>

				{entities.length === 0 ? (
					<div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50 px-6 py-16">
						<div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
							<CreditCard className="size-8 text-primary" />
						</div>
						<h3 className="mt-4 text-lg font-semibold text-base-content">
							No payment methods yet
						</h3>
						<p className="mt-2 max-w-sm text-center text-sm text-base-content/60">
							Add a payment method to fund payouts to your affiliate partners.
						</p>
						<Button className="mt-6" onClick={() => setIsAddDialogOpen(true)}>
							Add Payment Method
						</Button>
					</div>
				) : (
					<Table className="mt-8">
						<TableHead>
							<TableRow>
								<TableHeader>Name</TableHeader>
								<TableHeader>Card</TableHeader>
								<TableHeader>Created</TableHeader>
								<TableHeader>
									<span className="sr-only">Actions</span>
								</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{entities.map((entity) => (
								<TableRow key={entity.id}>
									<TableCell className="font-medium text-base-content">
										{entity.name}
									</TableCell>
									<TableCell className="text-base-content/60">
										{entity.paymentMethod ? (
											<span className="flex items-center gap-2">
												<span className="font-medium">
													{getCardBrandIcon(
														entity.paymentMethod.brand ?? "unknown",
													)}
												</span>
												<span>•••• {entity.paymentMethod.last4}</span>
												<span className="text-base-content/40">
													{entity.paymentMethod.expMonth}/
													{entity.paymentMethod.expYear}
												</span>
											</span>
										) : (
											<span className="text-warning">No card on file</span>
										)}
									</TableCell>
									<TableCell className="text-base-content/60">
										<Time date={entity.createdAt} />
									</TableCell>
									<TableCell>
										<div className="flex items-center justify-end gap-3">
											<Dropdown>
												<DropdownButton plain aria-label="More options">
													<EllipsisVertical className="size-5" />
												</DropdownButton>
												<DropdownMenu anchor="bottom end">
													<DropdownItem
														onClick={() => handleRename(entity.id, entity.name)}
													>
														Rename
													</DropdownItem>
													<DropdownItem
														onClick={() => handleReplace(entity.id)}
													>
														Replace Card
													</DropdownItem>
													<DropdownItem
														onClick={() => handleDelete(entity.id, entity.name)}
													>
														Delete
													</DropdownItem>
												</DropdownMenu>
											</Dropdown>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}

				<Fab
					label="Add Payment Method"
					onClick={() => setIsAddDialogOpen(true)}
				/>
			</div>
		</>
	);
}
